/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { DeviceModel } from '../../core/device';
import { SpinnerDelays } from '../../core/enums';
import { ErrorTypes, LiveTvError } from '../../core/error';
import { Locale } from '../../core/locale/Locale';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import { Log } from '../../core/logging';
import {
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../core/navigation';
import { AppDispatch, useTivoDispatch } from '../../core/tivo/tivo-redux-wrapper';
import { TVideoPlayerScreenProperties } from '../../core/types/TVideoPlayerScreenProperties';
import {
    MetadataState,
    PlaybackRestrictionType,
    SessionState,
    VideoPlayerStates,
} from '../../core/videoplayer/enums';
import { LiveTvPlaybackErrorMapper, PlaybackErrorDisplay } from '../../core/videoplayer/error';
import { IPlayable, IPlayerStatus } from '../../core/videoplayer/interfaces';
import { VideoPlaybackController } from '../../core/videoplayer/VideoPlaybackController';
import { hideSpinner, showSpinner } from '../../store/spinner/spinner.reducers';
import { hideErrorOverlay } from '../../store/videoplayer/overlays/erroroverlay/erroroverlay.reducers';
import {
    resetPlaybackParamsVideoplayer,
    resetStillThereTimeout,
    toggleIsPlaying,
} from '../../store/videoplayer/videoplayer.reducers';
import { setVideoPlayerState } from '../../store/videoplayer/videoplayerstate/videoplayerstate.reducers';
import { hideErrorOSD } from '../../store/videoplayerui/osd/osd.reducers';
import { useScreenController } from '../hooks';
import { VideoPlayerUI } from '../VideoPlayerUI';

const FullScreenPlayback: FunctionComponent = (): JSX.Element => {
    const TAG: string = 'FullScreenPlayback';
    const { screen } = useScreenController<TVideoPlayerScreenProperties>({
        identifier: TAG,
        properties: {
            url: LocationUrls.VIDEO_PLAYER,
            screenController: ScreenControllerTypes.VIDEO_PLAYER,
            taskController: ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS,
        },
    });
    const dispatch: AppDispatch = useTivoDispatch();

    // List of local states
    const [cleanupNeeded, setCleanupNeeded] = useState(false);
    const [playable, setPlayable] = useState<IPlayable | null>(
        VideoPlaybackController.getInstance().getPlayable(),
    );
    const videoPlaybackController = VideoPlaybackController.getInstance();
    const shouldShowLiveTVInitiationMessage = useRef(true);

    PlaybackErrorDisplay.dispatch = dispatch;

    const handlePlaybackError = (error: LiveTvError) => {
        if (!error) return;
        Log.d(TAG, 'handlePlaybackError error is', error);
        PlaybackErrorDisplay.getInstance().displayError(
            error,
            VideoPlaybackController.getInstance().getPlayable()?.playbackContentType,
        );
    };

    const handlePlayableChange = (_playable: IPlayable): void => {
        setPlayable(_playable);
    };

    const handleSessionStateChange = (state: SessionState) => {
        if (state === SessionState.ERROR) {
            const sessionError = VideoPlaybackController.getInstance().getSessionError();
            Log.d(TAG, 'handleSessionError error is', sessionError.data);
            if (sessionError.data) {
                PlaybackErrorDisplay.getInstance().displayError(
                    sessionError.data,
                    VideoPlaybackController.getInstance().getPlayable()?.playbackContentType,
                );
            }
        }
    };

    // TODO: KST-5305 - Which Restriction is this?
    // const handleStreamRestrictionChange = (status: boolean) => {
    //     Log.d(TAG, 'handleStreamRestrictionChange status is', status);
    //     PlaybackErrorDisplay.getInstance().setIsStreamingRestricted(status);
    // };

    const handleVideoStateChange = (status: IPlayerStatus): void => {
        Log.d(TAG, 'handleVideoStateChange state is', VideoPlayerStates[status.state]);

        switch (status.state) {
            case VideoPlayerStates.BUFFERING:
                dispatch(
                    showSpinner(
                        Locale.getString(LocaleKeys.LIVE_TV_SPINNER_LOADING_VIDEO),
                        SpinnerDelays.LIVE_TV,
                    ),
                );
                break;

            case VideoPlayerStates.ERROR_RECOVERY:
                if (
                    VideoPlaybackController.getInstance().isNetworkConnected() &&
                    !VideoPlaybackController.getInstance().isTrickplayEnabled()
                ) {
                    dispatch(
                        showSpinner(
                            Locale.getString(LocaleKeys.LIVE_TV_SPINNER_LOADING_VIDEO),
                            undefined,
                            status.code,
                        ),
                    );
                }
                break;

            case VideoPlayerStates.FATAL_ERROR:
                setCleanupNeeded(true);
                // TODO: KST-5305 How to handle this
                // handlePlaybackError(status.error);
                dispatch(hideSpinner());
                break;

            case VideoPlayerStates.FIRST_FRAME:
                break;

            case VideoPlayerStates.INITIATION:
                if (
                    shouldShowLiveTVInitiationMessage.current &&
                    VideoPlaybackController.getInstance().isLive()
                ) {
                    dispatch(
                        showSpinner(
                            Locale.getString(LocaleKeys.LIVE_TV_SPINNER_GOING_TO_LIVE_TV),
                            SpinnerDelays.LIVE_TV,
                        ),
                    );
                    shouldShowLiveTVInitiationMessage.current = false;
                } else if (
                    VideoPlaybackController.getInstance().isSocu() ||
                    VideoPlaybackController.getInstance().isNdvr() ||
                    VideoPlaybackController.getInstance().isVod()
                ) {
                    dispatch(
                        showSpinner(
                            Locale.getString(LocaleKeys.LIVE_TV_SPINNER_GETTING_YOUR_SHOW),
                            SpinnerDelays.LIVE_TV,
                        ),
                    );
                    shouldShowLiveTVInitiationMessage.current = true;
                } else
                    dispatch(
                        showSpinner(
                            Locale.getString(LocaleKeys.LIVE_TV_SPINNER_LOADING_VIDEO),
                            SpinnerDelays.LIVE_TV,
                        ),
                    );
                break;

            case VideoPlayerStates.LOADED:
                dispatch(setVideoPlayerState(VideoPlayerStates.LOADED));
                dispatch(hideErrorOverlay());
                if (
                    VideoPlaybackController.getInstance().getRestrictionType() !==
                    PlaybackRestrictionType.ADULT
                )
                    dispatch(hideErrorOSD(true));
                VideoPlaybackController.getInstance().updateCurrentPlayPosition();
                break;

            case VideoPlayerStates.LOADING:
                dispatch(setVideoPlayerState(VideoPlayerStates.LOADING));
                break;

            case VideoPlayerStates.PLAYING:
                dispatch(hideSpinner());
                break;

            case VideoPlayerStates.PAUSED_THRESHOLD:
                videoPlaybackController.resume();
                dispatch(toggleIsPlaying());
                break;

            case VideoPlayerStates.RATE_CHANGE:
                dispatch(hideSpinner());
                break;

            case VideoPlayerStates.UNLOADED:
                dispatch(setVideoPlayerState(VideoPlayerStates.UNLOADED));
                break;

            default:
        }
    };

    const handleMetadataStateChange = (state: MetadataState): void => {
        switch (state) {
            case MetadataState.AIRING_ERROR:
                {
                    const errorObj = new LiveTvPlaybackErrorMapper().getErrorByCode(
                        ErrorTypes.LIVE_TV_AIRING_DATA_FAILED_V139,
                    );
                    PlaybackErrorDisplay.getInstance().displayError(
                        errorObj,
                        VideoPlaybackController.getInstance().getPlayable()?.playbackContentType,
                    );
                }
                break;
            default:
        }
    };

    useEffect(() => {
        if (!DeviceModel.getAppSettings().isVideoWindowEnabled()) {
            VideoPlaybackController.getInstance().mount();
        }
        VideoPlaybackController.getInstance().maximize();

        VideoPlaybackController.getInstance()
            .getPlaybackErrorListener()
            .subscribe(handlePlaybackError);
        VideoPlaybackController.getInstance()
            .getSessionStateChangeListener()
            .subscribe(handleSessionStateChange);
        VideoPlaybackController.getInstance()
            .getMetadataStateChangeListener()
            .subscribe(handleMetadataStateChange);
        VideoPlaybackController.getInstance()
            .getPlayableChangeListener()
            .subscribe(handlePlayableChange);

        VideoPlaybackController.getInstance()
            .getPlayerStateChangeListener()
            .subscribe(handleVideoStateChange);

        const playerError = videoPlaybackController.getPlayerError();
        if (playerError !== null) {
            VideoPlaybackController.getInstance().restorePlayback();
        }
        if (KeyManager.getInstance().isKeyIgnoring()) KeyManager.getInstance().stopKeyIgnoring();
        return () => {
            VideoPlaybackController.getInstance()
                .getPlayableChangeListener()
                .cancel(handlePlayableChange);
            VideoPlaybackController.getInstance()
                .getPlaybackErrorListener()
                .cancel(handlePlaybackError);
            VideoPlaybackController.getInstance()
                .getPlayerStateChangeListener()
                .cancel(handleVideoStateChange);
            VideoPlaybackController.getInstance()
                .getMetadataStateChangeListener()
                .cancel(handleMetadataStateChange);
            VideoPlaybackController.getInstance().minimize();
            if (DeviceModel.inFrame()) {
                if (!DeviceModel.getAppSettings().isVideoWindowEnabled()) {
                    VideoPlaybackController.getInstance().unmount();
                }
            }
        };
    }, []);

    useEffect(() => {
        const properties: TVideoPlayerScreenProperties =
            screen?.getProperties() as TVideoPlayerScreenProperties;
        if (
            properties?.isForwardDirection &&
            !videoPlaybackController.isPlayerLoaded() &&
            playable
        ) {
            videoPlaybackController.startPlayback(playable);
        }
        screen.setProperties({ ...properties, isForwardDirection: false });
    }, [screen]);

    useEffect(() => {
        if (cleanupNeeded) {
            VideoPlaybackController.getInstance().clean();
        }
    }, [cleanupNeeded]);

    useEffect(() => {
        const playerError = videoPlaybackController.getPlayerError();
        if (playerError !== null) {
            VideoPlaybackController.getInstance().restorePlayback();
        }
        if (!VideoPlaybackController.getInstance().isNetworkConnected()) return;
        dispatch(resetStillThereTimeout(true));
        dispatch(hideErrorOSD());
        dispatch(hideErrorOverlay());
        dispatch(resetPlaybackParamsVideoplayer());
    }, [playable]);

    return (
        <React.Fragment>
            <VideoPlayerUI />
        </React.Fragment>
    );
};

export default FullScreenPlayback;
